import { memo, useEffect, useRef, useState } from 'react';
import { allPages, movePagePhotos, type Issue, type PageLayout, type ProjectDoc } from '@cal/engine';
import { PageSvg, type Geometry } from '../render/PageSvg';
import { edit, useEditor } from './store';
import { useGeometry, type useDocResources } from './hooks';
import { ASSET_MIME, assignPhoto } from './Canvas';

const PAGE_MIME = 'application/x-calendar-page';
const HINT_KEY = 'cal.stripHintSeen';

/** Thumbnail strip. Drag one month onto another to move its photograph there (others shift). */
export function Strip({ doc, layouts, issues, res, overrides }: { doc: ProjectDoc; layouts: Map<string, PageLayout>; issues: Issue[]; res: ReturnType<typeof useDocResources>; overrides: (k: string) => boolean }) {
  const page = useEditor((s) => s.page);
  const geometry = useGeometry(doc);
  const [over, setOver] = useState<string | null>(null);
  const pages = allPages(doc);
  const [hint, setHint] = useState(() => { try { return localStorage.getItem(HINT_KEY) !== '1'; } catch { return true; } });
  const hideHint = () => { setHint(false); try { localStorage.setItem(HINT_KEY, '1'); } catch { /* storage unavailable */ } };
  return (
    <div className="strip">
      <nav aria-label="Pages" className="strip-pages">
      {pages.map((p) => {
        const l = layouts.get(p.key);
        const errs = issues.some((i) => i.pageKey === p.key && i.severity === 'error');
        const movable = p.kind === 'month' || p.kind === 'cover' || p.kind === 'face';
        return (
          <button key={p.key} className={`thumb ${over === p.key ? 'drop' : ''}`} aria-current={page === p.key ? 'page' : undefined} aria-label={`${p.label}${errs ? ' (has blocking issues)' : ''}`}
            draggable={movable}
            onClick={() => useEditor.setState({ page: p.key, view: 'single' })}
            onDragStart={(e) => { e.dataTransfer.setData(PAGE_MIME, p.key); e.dataTransfer.effectAllowed = 'move'; }}
            onDragOver={(e) => { if (movable && (e.dataTransfer.types.includes(PAGE_MIME) || e.dataTransfer.types.includes(ASSET_MIME))) { e.preventDefault(); setOver(p.key); } }}
            onDragLeave={() => setOver(null)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(null);
              const from = e.dataTransfer.getData(PAGE_MIME);
              const asset = e.dataTransfer.getData(ASSET_MIME);
              if (from && from !== p.key) { edit((d) => movePagePhotos(d, from, p.key), 'reorder'); hideHint(); }
              else if (asset) assignPhoto(p.key, 0, asset);
            }}>
            {overrides(p.key) && <span className="dot" title="This page has monthly changes" />}
            {errs && <span className="errdot" title="Blocking issues" />}
            <LazyThumb layout={l} geometry={geometry(p.key)} fontVersion={res.fontVersion} label={p.label} />
            <span className="lbl">{p.kind === 'month' ? p.label.slice(0, 3) : p.label}</span>
          </button>
        );
      })}
      </nav>
      {doc.product.productType === 'monthly' && hint && (
        <span className="small faint row" style={{ alignSelf: 'center', whiteSpace: 'nowrap', paddingLeft: 8, gap: 4 }}>
          Drag a month onto another to reorder photographs
          <button type="button" className="btn sm icon" aria-label="Hide this tip" title="Hide this tip" onClick={hideHint}>×</button>
        </span>
      )}
    </div>
  );
}

const LazyThumb = memo(function LazyThumb({ layout, geometry, fontVersion, label }: { layout?: PageLayout; geometry: Geometry; fontVersion: number; label: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setVisible(true), { rootMargin: '200px' });
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  const w = 64;
  const h = layout ? (w * layout.trimH) / layout.trimW : w * 1.41;
  return (
    <div ref={ref} style={{ width: w, height: h }}>
      {visible && layout && <PageSvg layout={layout} geometry={geometry} widthPx={w} quality="thumb" showBleed={false} fontVersion={fontVersion} ariaLabel={`${label} thumbnail`} />}
    </div>
  );
});
